import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import awardService from '../../services/awards';
import type { AwardCode, AwardReference, AwardRuleList, AwardSubjectKind } from '../../types/awards';
import { SUBJECT_KIND_LABEL, levelLabel } from '../../utils/awardReviews';

/**
 * What moves someone up: the organisation's mapping of machines, types of
 * work, certifications and spraying to award levels (Timesheet HR seat).
 * Holding one raises a "new skill above their level" reminder; doing one for
 * time raises "higher duties". Our suggestions are a starting point to edit.
 */

function errText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message || e?.message || fallback;
}

const KINDS: AwardSubjectKind[] = ['equipment_type', 'equipment', 'activity_type', 'certification', 'spray_operator'];
const selectCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

export default function AwardRulesPanel({ reference, defaultAward, onChanged }: {
  reference: AwardReference | null;
  defaultAward: AwardCode | null;
  /** Rules changed: reminders may now be due. */
  onChanged?: () => void;
}) {
  const [list, setList] = useState<AwardRuleList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [award, setAward] = useState<AwardCode | ''>(defaultAward || '');
  const [form, setForm] = useState<{ level_key: string; subject_kind: AwardSubjectKind; subject_ref: string }>(
    { level_key: '', subject_kind: 'equipment_type', subject_ref: '' });

  useEffect(() => { if (!award && defaultAward) setAward(defaultAward); }, [award, defaultAward]);
  useEffect(() => {
    if (!award && reference?.awards[0]) setAward(reference.awards[0].code);
  }, [award, reference]);

  const load = useCallback(async () => {
    try {
      const res = await awardService.rules();
      if (res.success && res.data) setList(res.data);
      else setError('Could not load the rules.');
    } catch (err) {
      setError(errText(err, 'Could not load the rules.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<{ success: boolean; data?: AwardRuleList }>, done: (d: AwardRuleList) => string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      if (res.success && res.data) {
        setList(res.data);
        setNotice(done(res.data));
        onChanged?.();
        return true;
      }
      setError('That did not save. Try again.');
    } catch (err) {
      setError(errText(err, 'That did not save. Try again.'));
    } finally {
      setBusy(false);
    }
    return false;
  };

  const awardRef = reference?.awards.find(a => a.code === award) || null;
  const rules = useMemo(() => (list?.rules || []).filter(r => r.award_code === award), [list, award]);
  const opts = list?.options;

  const subjectChoices: { value: string; label: string }[] = useMemo(() => {
    if (!opts) return [];
    switch (form.subject_kind) {
      case 'equipment_type': return opts.equipment_types.map(t => ({ value: t.type_key, label: t.label }));
      case 'equipment': return opts.equipment.map(e => ({ value: String(e.id), label: e.name }));
      case 'activity_type': return opts.activity_types.map(a => ({ value: String(a.id), label: a.name }));
      case 'certification': return opts.certification_types.map(c => ({ value: String(c.id), label: c.name }));
      default: return [];
    }
  }, [opts, form.subject_kind]);

  const needsSubject = form.subject_kind !== 'spray_operator';
  const canAdd = !!award && !!form.level_key && (!needsSubject || !!form.subject_ref);

  const add = async () => {
    if (!award) return;
    const ok = await act(() => awardService.addRule({
      award_code: award,
      level_key: form.level_key,
      subject_kind: form.subject_kind,
      subject_ref: needsSubject ? form.subject_ref : null,
    }), () => 'Rule added.');
    if (ok) setForm(f => ({ ...f, subject_ref: '' }));
  };

  // Highest level first, the way a reader asks "what makes someone a Level 3".
  const byLevel = useMemo(() => {
    const levels = awardRef ? [...awardRef.levels].reverse() : [];
    return levels.map(l => ({ level: l, rules: rules.filter(r => r.level_key === l.key) })).filter(g => g.rules.length);
  }, [awardRef, rules]);

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-neutral-900">What moves someone up</h2>
          <p className="text-xs text-neutral-500 mt-1 break-words">
            Match machines, types of work, certifications and spraying to a level. When someone is signed off on one, or gains
            the certification, you are told they now hold a skill above their level. When they spend time doing it, you are told
            about higher duties for that day. Holding a skill is not being required to use it, so these are prompts, not decisions.
          </p>
        </div>

        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{notice}</div>}

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto sm:min-w-[16rem]">
            <label htmlFor="rules-award" className="block text-xs text-neutral-600 mb-1">Award</label>
            <select id="rules-award" className={selectCls} value={award}
              onChange={e => setAward(e.target.value as AwardCode)}>
              {reference?.awards.map(a => <option key={a.code} value={a.code}>{a.name}</option>)}
            </select>
          </div>
          {award && (
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => act(() => awardService.addSuggested(award as AwardCode),
                d => (d.added ? `${d.added} suggested rule${d.added === 1 ? '' : 's'} added. Check them against your own roles.` : 'Every suggested rule is already there.'))}>
              Add our suggested rules
            </Button>
          )}
        </div>
        {award === 'MA000028' && (
          <p className="text-xs text-neutral-500 break-words">
            Tractors over 70 kW are Level 3 work and smaller ones Level 2. Tractor power is not recorded, so the suggestions put tractors
            at Level 2: add your bigger tractors one machine at a time at Level 3. Licences (Level 4) are your own certification types.
          </p>
        )}

        {!list ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : byLevel.length === 0 ? (
          <p className="text-sm text-neutral-600">No rules for this award yet, so no skill or duties reminders are raised.</p>
        ) : (
          <div className="space-y-3">
            {byLevel.map(g => (
              <div key={g.level.key}>
                <h3 className="text-sm font-semibold text-neutral-800">{g.level.label}</h3>
                <ul className="mt-1 flex flex-wrap gap-2">
                  {g.rules.map(r => (
                    <li key={r.id} className="flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 pl-3 pr-1 py-0.5 text-sm max-w-full min-w-0">
                      <span className="text-xs text-neutral-500 shrink-0">{SUBJECT_KIND_LABEL[r.subject_kind]}:</span>
                      <span className="break-words min-w-0">{r.subject_label}</span>
                      <button
                        type="button"
                        className="p-1 text-neutral-400 hover:text-red-600 shrink-0"
                        aria-label={`Remove ${r.subject_label} from ${g.level.label}`}
                        disabled={busy}
                        onClick={() => act(() => awardService.deleteRule(r.id), () => 'Rule removed.')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {list && awardRef && (
          <div className="border-t border-neutral-200 pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-800">Add a rule</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="rule-kind" className="block text-xs text-neutral-600 mb-1">What</label>
                <select id="rule-kind" className={selectCls} value={form.subject_kind}
                  onChange={e => setForm(f => ({ ...f, subject_kind: e.target.value as AwardSubjectKind, subject_ref: '' }))}>
                  {KINDS.map(k => <option key={k} value={k}>{SUBJECT_KIND_LABEL[k]}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="rule-subject" className="block text-xs text-neutral-600 mb-1">Which</label>
                {needsSubject ? (
                  <select id="rule-subject" className={selectCls} value={form.subject_ref}
                    onChange={e => setForm(f => ({ ...f, subject_ref: e.target.value }))}>
                    <option value="">Choose</option>
                    {subjectChoices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                ) : (
                  <p id="rule-subject" className="text-sm text-neutral-600 py-2">Any spray they operate</p>
                )}
              </div>
              <div>
                <label htmlFor="rule-level" className="block text-xs text-neutral-600 mb-1">Level</label>
                <select id="rule-level" className={selectCls} value={form.level_key}
                  onChange={e => setForm(f => ({ ...f, level_key: e.target.value }))}>
                  <option value="">Choose</option>
                  {awardRef.levels.map(l => <option key={l.key} value={l.key}>{levelLabel(reference, award, l.key)}</option>)}
                </select>
              </div>
            </div>
            {needsSubject && subjectChoices.length === 0 && (
              <p className="text-xs text-neutral-500">Nothing of that kind is recorded yet.</p>
            )}
            <Button size="sm" disabled={busy || !canAdd} onClick={add}>Add rule</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
